import { useState, useRef, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiX } from 'react-icons/fi';
import './Calendar.css';

const months = [
  'იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
  'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'
];

const weekDays = ['ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ', 'კვი'];

const Calendar = ({ label, value, onChange, placeholder = 'აირჩიეთ თარიღი', max, min, compact, highlightDates }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const today = new Date();
  const selected = value ? new Date(value) : null;
  const [viewMonth, setViewMonth] = useState(selected ? selected.getMonth() : today.getMonth());
  const [viewYear, setViewYear] = useState(selected ? selected.getFullYear() : today.getFullYear());
  const [showYearPicker, setShowYearPicker] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const yearListRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setShowYearPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const calcPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const popupHeight = 360;
    const openUp = spaceBelow < popupHeight && rect.top > popupHeight;

    setPopupStyle({
      position: 'fixed',
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 308)),
      width: 300,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
  };

  useEffect(() => {
    if (isOpen) calcPosition();
  }, [isOpen]);

  useEffect(() => {
    if (showYearPicker && yearListRef.current) {
      const activeEl = yearListRef.current.querySelector('.year-item.active');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'center' });
      }
    }
  }, [showYearPicker]);

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const selectDate = (day) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange(`${viewYear}-${m}-${d}`);
    setIsOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const formatDisplay = () => {
    if (!selected) return '';
    const d = String(selected.getDate()).padStart(2, '0');
    const m = String(selected.getMonth() + 1).padStart(2, '0');
    return `${d}/${m}/${selected.getFullYear()}`;
  };

  const renderDays = () => {
    const daysInMonth = getDaysInMonth(viewMonth, viewYear);
    const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
    const prevDays = getDaysInMonth(viewMonth === 0 ? 11 : viewMonth - 1, viewMonth === 0 ? viewYear - 1 : viewYear);
    const cells = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push(<div key={`p${i}`} className="cal-day outside">{prevDays - i}</div>);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const isToday = d === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
      const isSelected = selected && d === selected.getDate() && viewMonth === selected.getMonth() && viewYear === selected.getFullYear();
      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isDisabled = (max && dateStr > max) || (min && dateStr < min);
      const isHighlighted = highlightDates && highlightDates.includes(dateStr);
      cells.push(
        <div
          key={d}
          className={`cal-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${isHighlighted ? 'highlighted' : ''}`}
          onClick={() => !isDisabled && selectDate(d)}
        >
          {d}
        </div>
      );
    }

    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
      cells.push(<div key={`n${i}`} className="cal-day outside">{i}</div>);
    }

    return cells;
  };

  const years = [];
  for (let y = today.getFullYear(); y >= 1920; y--) {
    years.push(y);
  }

  return (
    <div className={`calendar-wrapper ${compact ? 'calendar-compact' : ''}`} ref={ref}>
      {label && <label className="calendar-label">{label}</label>}
      <div
        ref={triggerRef}
        className={`calendar-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => { setIsOpen(!isOpen); setShowYearPicker(false); }}
      >
        <FiCalendar className="cal-icon" />
        <span className={value ? '' : 'placeholder'}>{formatDisplay() || placeholder}</span>
        {value && <FiX className="cal-clear" onClick={clear} />}
      </div>

      {isOpen && (
        <div className="calendar-popup" style={popupStyle}>
          {showYearPicker ? (
            <div className="year-picker" ref={yearListRef}>
              {years.map((y) => (
                <div
                  key={y}
                  className={`year-item ${y === viewYear ? 'active' : ''}`}
                  onClick={() => { setViewYear(y); setShowYearPicker(false); }}
                >
                  {y}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="cal-header">
                <button type="button" className="cal-nav" onClick={prevMonth}><FiChevronLeft /></button>
                <div className="cal-title" onClick={() => setShowYearPicker(true)}>
                  {months[viewMonth]} {viewYear}
                </div>
                <button type="button" className="cal-nav" onClick={nextMonth}><FiChevronRight /></button>
              </div>
              <div className="cal-weekdays">
                {weekDays.map((d) => <div key={d} className="cal-weekday">{d}</div>)}
              </div>
              <div className="cal-grid">
                {renderDays()}
              </div>
              <div className="cal-footer">
                <button type="button" className="cal-today-btn" onClick={() => {
                  setViewMonth(today.getMonth());
                  setViewYear(today.getFullYear());
                  selectDate(today.getDate());
                }}>
                  დღეს
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Calendar;
